if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (!this.elements.viewer) return;

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );

          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              mediaToSwitch
                .querySelector('button')
                ?.addEventListener(
                  'click',
                  this.setActiveMedia.bind(
                    this,
                    mediaToSwitch.dataset.target,
                    false
                  )
                );
            });

          if (
            this.dataset.desktopLayout?.includes('thumbnail') &&
            this.mql.matches
          ) {
            this.removeListSemantic();
          }
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) return;

        const isMobile = !this.mql.matches;

        /*
         * Remove active state from every gallery item.
         */
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        /*
         * Ladies Fit Overs:
         * Move selected model/variant image to slide 1.
         */
        if (prepend) {
          const mediaList = activeMedia.parentElement;

          if (
            mediaList &&
            mediaList.firstElementChild !== activeMedia
          ) {
            mediaList.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement &&
              activeThumbnail.parentElement.firstElementChild !==
                activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }
        }

        this.preventStickyHeader();

        /*
         * Wait for the browser to finish recalculating the
         * reordered gallery before resetting the slider.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.elements.viewer.resetPages?.();

            const mediaList =
              activeMedia.parentElement;

            if (mediaList) {
              if (prepend) {
                /*
                 * Selected image is now slide 1.
                 */
                mediaList.scrollTo({
                  left: 0,
                  behavior: 'auto',
                });
              } else {
                mediaList.scrollTo({
                  left: activeMedia.offsetLeft,
                  behavior: 'auto',
                });
              }
            }

            /*
             * Mobile:
             * Stay at the customer's current page position.
             * Do NOT automatically scroll back up to gallery.
             */

            /*
             * Final refresh keeps Shopify's counter and
             * navigation arrows synced after the reorder.
             */
            requestAnimationFrame(() => {
              this.elements.viewer.resetPages?.();
            });
          });
        });

        /*
         * Desktop only:
         * Preserve Dawn's normal vertical behavior.
         */
        if (!isMobile) {
          window.setTimeout(() => {
            const activeMediaRect =
              activeMedia.getBoundingClientRect();

            if (activeMediaRect.top > -0.5) return;

            const top =
              activeMediaRect.top + window.scrollY;

            window.scrollTo({
              top,
              behavior: 'smooth',
            });
          });
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute('aria-current')
          );

        thumbnail
          .querySelector('button')
          ?.setAttribute('aria-current', true);

        requestAnimationFrame(() => {
          if (
            this.elements.thumbnails.isSlideVisible?.(
              thumbnail,
              10
            )
          ) {
            return;
          }

          if (this.elements.thumbnails.slider) {
            this.elements.thumbnails.slider.scrollTo({
              left: thumbnail.offsetLeft,
              behavior: 'auto',
            });
          }
        });
      }

      /*
       * GLOREL FIX:
       *
       * Do NOT use:
       *
       * image.src = image.src;
       *
       * Reassigning the same image URL can force mobile
       * browsers to reload/redecode/repaint the image,
       * creating the visible flicker during variant changes.
       */
      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        /*
         * If the image is already loaded,
         * announce immediately without touching src.
         */
        if (
          image.complete &&
          image.naturalWidth > 0
        ) {
          announce();
          return;
        }

        /*
         * If it is still loading, wait for the real load.
         * Do not force another reload.
         */
        image.addEventListener(
          'load',
          announce,
          { once: true }
        );
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems?.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}